import React, { useState } from "react";
import { Icon } from "./Icons";
import "../styles/ClientManagerTimesheet.css";

const ClientManagerTimesheet = ({ timesheets, onStatusChange, showActionButtons = true }) => {
  const sorted = (timesheets || []).slice().sort((a, b) => new Date(b.week_start) - new Date(a.week_start));
  const singleMode = sorted.length === 1;

  // In list mode start collapsed; in single mode start expanded
  const [expandedId, setExpandedId] = useState(() => singleMode ? sorted[0]?.id : null);
  const [statusOverrides, setStatusOverrides] = useState({});

  const daysOrder = ["sun","mon","tue","wed","thu","fri","sat"];

  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const formatDate = (date) => `${date.getMonth() + 1}/${date.getDate()}`;

  const getWeekDates = (weekStart) => {
    const start = parseLocalDate(weekStart);
    const sun = new Date(start);
    sun.setDate(start.getDate() - start.getDay());
    return daysOrder.map((_, idx) => {
      const d = new Date(sun);
      d.setDate(sun.getDate() + idx);
      return d;
    });
  };

  const updateStatus = async (ts, newStatus) => {
    if ((statusOverrides[ts.id] ?? ts.status) !== "Pending") return;
    try {
      const token = sessionStorage.getItem("jwtToken");
      const res = await fetch(`/timesheets/operations/updateTimeSheetStatus/${ts.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setStatusOverrides(prev => ({ ...prev, [ts.id]: updated.status }));
      onStatusChange?.(ts.id, updated.status);
    } catch (err) {
      console.error("Error updating timesheet:", err);
    }
  };

  /* ── Breakdown block — shared between single and list mode ── */
  const Breakdown = ({ ts }) => {
    const status = statusOverrides[ts.id] ?? ts.status;
    const weekDates = getWeekDates(ts.week_start);
    return (
      <div className="details-dropdown">
        <h4>Daily Breakdown (Sun - Sat)</h4>
        <table className="nested-table">
          <thead>
            <tr>
              {daysOrder.map((day, i) => (
                <th key={day}>
                  {day.toUpperCase()}<br/>
                  <span className="date-label">({formatDate(weekDates[i])})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {daysOrder.map(day => (
                <td key={day}>{ts.daily_hours?.[day] ?? 0}</td>
              ))}
            </tr>
          </tbody>
        </table>
        <div className="dropdown-actions">
          {(ts.clientComment || ts.comment) && (
            <div style={{marginBottom:10,padding:"8px 12px",background:"#F8FBFF",border:"1px solid #DBEAFE",borderRadius:8,fontSize:"0.82rem",color:"#1E293B",whiteSpace:"pre-wrap",wordBreak:"break-word",lineHeight:1.45}}>
              <span style={{fontWeight:700,color:"#2563EB",fontSize:"0.75rem",display:"block",marginBottom:3}}>Note:</span>{ts.clientComment || ts.comment}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ── Single mode: show breakdown directly, no extra row ── */
  if (singleMode) {
    return (
      <div className="clientmanager-timesheet" style={{ border: "none", boxShadow: "none" }}>
        <Breakdown ts={sorted[0]} />
      </div>
    );
  }

  /* ── List mode: show table with expand/collapse per row ── */
  return (
    <div className="clientmanager-timesheet">
      <table>
        <thead>
          <tr>
            <th>Week Start</th>
            <th>Week End</th>
            <th>Total Hours</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(ts => {
            const status = statusOverrides[ts.id] ?? ts.status;
            const isOpen = expandedId === ts.id;
            return (
              <React.Fragment key={ts.id}>
                <tr className="ts-row">
                  <td>
                    <div className="ts-date-cell">
                      <span className="ts-dot" /><span className="ts-date-text">{ts.week_start}</span>
                    </div>
                  </td>
                  <td>
                    <div className="ts-date-cell">
                      <span className="ts-dot" /><span className="ts-date-text">{ts.week_end}</span>
                    </div>
                  </td>
                  <td><span className="hours-badge">🕐 {ts.total_hours}h</span></td>
                  <td><span className={`status-badge ${status.toLowerCase()}`}>{status}</span></td>
                  <td>
                    <button className="view-btn" onClick={() => setExpandedId(isOpen ? null : ts.id)}>
                      {isOpen
                        ? <><Icon.X size={13} color="currentColor" /> Hide</>
                        : <><Icon.Eye size={13} color="currentColor" /> View</>}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="dropdown-row">
                    <td colSpan="5">
                      <Breakdown ts={ts} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClientManagerTimesheet;
